/**
 * The picture the encoder probe encodes, and nothing else.
 *
 * Task `0129` needs a fixed specimen to put through `createImageBitmap` +
 * `OffscreenCanvas` + `convertToBlob`, so the derivative key can name what
 * produced the bytes. It lives in its own file because it is sixty lines of
 * base64 and `derivatives.ts` is a file people read.
 *
 * ## It is reproducible, which a pasted blob usually is not
 *
 * Generated by exactly this, and regenerating it CHANGES EVERY EXISTING KEY —
 * the probe's digest is a digest of encoding this picture. Do not regenerate it
 * to tidy something up.
 *
 * ```py
 * import zlib, struct, base64
 * N = 32
 * state = 0x2545F491
 * def nxt():
 *     global state
 *     state = (state * 1103515245 + 12345) & 0xFFFFFFFF
 *     return (state >> 16) & 0xFF
 * rows = bytearray()
 * for y in range(N):
 *     rows.append(0)                       # PNG filter type 0
 *     for x in range(N):
 *         n = nxt() >> 3                   # 0..31, enough to defeat prediction
 *         rows += bytes(((x * 8 + n) & 0xFF, (y * 8 + n) & 0xFF,
 *                        ((x ^ y) * 8 + n) & 0xFF))
 * def chunk(tag, data):
 *     return (struct.pack('>I', len(data)) + tag + data
 *             + struct.pack('>I', zlib.crc32(tag + data) & 0xFFFFFFFF))
 * png = (b'\x89PNG\r\n\x1a\n'
 *        + chunk(b'IHDR', struct.pack('>IIBBBBB', N, N, 8, 2, 0, 0, 0))
 *        + chunk(b'sRGB', b'\x00')
 *        + chunk(b'IDAT', zlib.compress(bytes(rows), 9))
 *        + chunk(b'IEND', b''))
 * print(base64.b64encode(png).decode())
 * ```
 *
 * A ramp under a deterministic LCG's noise: the ramp gives an encoder's
 * prediction something to do, the noise gives it something it cannot do. A flat
 * or smoothly-ramped specimen is one two different encoders agree on while
 * disagreeing about every photograph — a specimen that cannot carry the
 * property under test, which is a failure this repository has already paid for.
 *
 * `sRGB` is declared rather than left absent so the colour pipeline has a
 * stated input; 3,185 bytes, 4,248 as base64.
 */
export const PROBE_PNG_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAIAAAD8GO2jAAAAAXNSR0IArs4c6QAADCtJREFU'
  + 'eNoBIAzf8wAODg4VDRUsHCwwGDApCSlEHEQ7CztDC0NaGlpmHmZhEWFxGXF5GXlpAWl+Dn6P'
  + 'F4+bG5uSCpKuHq6wGLCoCKiuBq63B7fLE8vfH9/jG+PtHe3sFOz9Hf3uBu7zA/MKEgoAEhoa'
  + 'ISEZLSU1KxsjMxs7NhYuTydXQhI6RQ1NXBxUYxtraxtjYQlpeRlxhR2NliaOhg6OoyObmhKi'
  + 'siKqtBy8uhqyvBTEyhrCzBTUzQ3F4Rnp3g7W+CAAAyP7+REBESEJAAQUFBAYIB8fDzIqIiwc'
  + 'PC8XP04uPjwULEkZWUoSWl8fT2sjW3EhgX4mjosre44mfo4enpcfp6AgkK0lnbYmxsMr080t'
  + 'vcgguM4e3t4m7ukp2fIq4vAgAPQcBA8v//wU7AAAGBggMCgWHg4dHQUiGjo8LEQ6IjI4GCBC'
  + 'GlpNHVVnL19pKVFzK4toGHByGmp9HWWNJaWMHJSTG4u3N5+6MtK4KMDNNcXMLLTDG9vTI9vX'
  + 'H8/fH8fwKAjyIvr5IfERMfkAACAgEioyIzNDIytDPz8fLSUNNiYWPCQcSipqZT2Fbz+PWyN7'
  + 'czNThT1lfy9fizNrnj6+ojrCpzfHpy/HqiqKqSGJvi6euyObxSXlzSXt3S393CT84yPD6yPL'
  + '+ira+CDYAAAoKCREPBoyQhsrMy42FjExCUY+LlBAKFxEhFAwaGlBkVkpcXxEZIVFXXQsXH4u'
  + 'VpxExJc3r65G1rREzLI6msREnMU9rb0tlc429soq4tEp+eAw+PQ83O4uxvQs3Pkp0QAbS0sV'
  + 'PUUuTj4cNCw5SSk3PydMTBw7MwtTQ4NPN39qSnpZMWl5SWl3P2d0NER9NU2BMbGLM7ucPKy1'
  + 'TcW8TKy5QanDQ5O6MoraSgrKMvrjQ/PjO/PvP9/qMtoKStr5MckAC0NDJFRMEjoqHDwkLUUl'
  + 'Lj4WS1MjQ0MLS0ODX0+HY0t7c1N7dExsajpSgkpakFBYikLCnU3Fo0u7okKqtEystkaevESU'
  + '11efxDz8yzvz50//2jri/VX17z/XAUnZAEDIAAtLSxBIUBlJWTNbczNTc0VdhUZWhlBYkF1d'
  + 'HWBYIGdXJ11FHXlZOWxELIFRQX1FPZFR0YtDy5dH17FZ8aRE5KpC6rND871F/cdHh8xEjNFB'
  + 'kd1FnetLq+tDq/lJuRRc1AAXX18YWFAWTl43Z283X38rS2NBWYlIWIBHTw9RUQlrYzNvXydy'
  + 'WjptTSV1TT2LW0ORWdmVVc2qYvKZSdG3X//FZf3LYxPSYgrdZaXMTITTS5vjU5vjS6vtTaX4'
  + 'UMD+TrYAClpaG2NrHFxMIVlRJFR0L1d/P19vU2uDQVERUVkhYGAQX1cPa1s7fGRMf18vgloy'
  + 'hVXVmWHpr2/fsWnhuWkJtl4Gxmb2vVXtwlKSy1Ob4GCQ41uT8GDABGzU/1+vBFy0AB52dhho'
  + 'YB5mViZmTiVdfUBwiEFpeThYYEhgIF5uJlpiEmVlDW9nR4d3T4NrO4hoMIhg4Jdn35pi0rFx'
  + '2bRsDKpa8slxAcVl7dFpqed3r+x0pPR0nP111f9vx/FZqQxstAAPb28OZm4vf48eZn4lZUUs'
  + 'ZExMfGw9ZV1WdjZZcTlre0t3f1doaAh+dh6FdSWFbSWVdfWNZe2ebv6vdw+zc9Oza9O1ZdXR'
  + 'efHFZaXfd7/oeMjtdc32dpbtZY3zY5P+Zp4AF39/HHx0InqKIHB4L3dXRIRcQ3trSnpiRm4u'
  + 'X383aIBQbX1FYmoKb28Hh38vfm4Wh2/vm3vzrYUVonL6omrKvX3Vzob2yHjg2IDA03Or6YHR'
  + '5XW95GyM/n6WBX2tAnKaABGBgSKKkiqKeiJ6ci5+XjV9ZUeHV0V9VVODQ1B4QGKCMl11LWh4'
  + 'GGtzG4uLG311DZmJCaCIEJp66qJ68qx83LmB6bd3x9KK4sZ2tuGJ0eGBsfOLw/SEpACIsA2N'
  + 'nf11jQAZkZEdjYUZgXEcfGQthWUwgFg9hVVWll5ZkVFKejJtlUVffydkfBx/jyd2fg6Ghg6G'
  + 'fv6aigKZgfGkhOy6kvK2ht6ze8vUlNzLg8PIeLDji7vujrbvh6cCkqoMlKQXl58ACoqKDYWN'
  + 'JJSkJY2lNpa2QprCSJjIRIzEUpLSTobOW4vbaZHpYoLiepL6ipoKgooCiYkJiIAInIwcoYkh'
  + 'uZk5wppCxJRE159X359fyIBI4pJi7ZVt4oJi84tzBZWFFp6WAB6mphKSih2VpTSkrDaevjub'
  + 's0mh0VWlzVmh4VmZ0WCY6HKi6m2V9W2N5YqiEniI8J2lJYqKApePH6+fJ7ScPKyMJM+nV8CQ'
  + 'OMCISN6eVtKKWueXX/effwKievyUhAOTewARoaEJkZkWloYfl482psYqkrpFpbVRqcFKmtpL'
  + 'k9tvr99yquJvn/+FrRV5memNpf2drS2fpy+cnAymnha1pUXErFS1lSW4kCjHl1fMlFzlpVXz'
  + 'q2P6qor/p4/0lGQDm3MAHbW1E6ObJKycHp6GIpq6P6/HPqa2P5+nX7f3Y7PrW6PTYKDIdKwM'
  + 'cqL6eqLyiqryhJwcpbUtn6cXm5sDp58/uKhAuqIyxaUt0Khozp5W7rZm6KhQ9KyM96d/D7eH'
  + '/59nAB6+vg6mrhmpuSiwyCWlhUe/pziomEWtpVOz82C4AFam9nK6Emenx4K64nCg0Ja+9o6u'
  + 'Lpy0PJKiMqevR7a2FriwGMGxIc62Lta2duW9heCwgN2lffGxUfuzW/GhUfykXAAOtrYYuLAn'
  + 'v88zw8sqspIysoo1rZ1Lu6NBqelgwPhUrPxgsPhyutqFxd1/t+eXx++UvDyiwjqpwVGgsDis'
  + 'tBSysgrNxTW7qxPUvHzPr2fvx5fpuYH9xWX5uVH9tWUCsloACbm5ErrCI8OzGLCoPMysL7ef'
  + 'OLiIV8+nRbX1ZMwUZ8f3YLjwYbHRarLaiMjYlMzkksJCl79HlrYmnbUttMQktr4mwMAQ0ckh'
  + 'xLR0y7N77Mx847tz4rJSAMhw9bVF+LBIAArCwhrKwh7GtizMtCa+ni29lUXNnVHRmVnREUm5'
  + '8WTM/GrK8n7W9n/P54/X55TU3IO7O5rKQq3VRbbWPrrSMrHBGb/HF7i4AMO7e9nJgezUhOLC'
  + 'auvDYwDQaPzEVAXFTQAKysoPx88s3Ownz+cz0/M60vo+zv5AyABY2JhPx49czJx03LR11bV0'
  + 'zLR4yLh/x7+Dw0OJwUmg0GChyWGpyWm1zXXL24u4wHjKygrXzxfh0SHdxR3jwyMG3kb3xzcQ'
  + '2FAAB8/PENDIIdnpH8/XLdX1R+f/Rt4OStoCXuamVdWNY9urdeWtcdm5eNiwhd3Ne8uzi9NT'
  + 'o+NbotpqteVtqtJyr89nyOCQ1+eP1Nwc5OQc39cn69sj9d09A+M7Bt5O/883ABrq6hzk7C7u'
  + '3izk3CHR8ULqEkHh8VfvB0XVlVnhqWvrm2XdlX3tzXriynjYqJDowJfnZ4jQWKfnV6nhWara'
  + 'eq7WfrnZabzUbN7uLuDoMNfXB+PbE+3dPQXtVQ/vP/vTKwAe9vYS4ton798l5c048BA56QEx'
  + '2elN7fVI4KBc7KRp8aFb24Nj27to2LB23q6E5KyC2lqX51+h6Vm19V207IzC8orB6XnN7XXB'
  + '2RnJ2RHe5hbc3AT07Ez760Pz2ysJ6TEAD+/vEenxIPAAIekBPv7ePfXdOenZVf31QuKiW/O7'
  + 'bv7ObfXNbu6OcuqSgfGhfuaeiOhojuZupPSErvaOsvJSufFZw/Nj1Px02fk53vY+5vZG7fVN'
  + '9/cX9e0V9eUVEfkxAAjw8Bj48CoCEjcHDz8H5zv70z315VMD6072tlb2rm0F1XICymvzk4YG'
  + 'nnz0pIb2noLqao3tZaP7i639ha/3V8ICWsH5adUFbcHpKd7+NuL6Su39ReDoCAAAGPnxIfrq'
  + 'EgAD8/MmDhYZ+ekd9e0k9NREDPQ//89RCeFI+LhM9Lxb+6ti+rJj85N0/KSKCpqC+pKN/X2S'
  + '+oKvD3+pAXm3B2et9V2//0+48EjUBETJ8TnlBTXzC0P6Cirt9R0CAhILAxsAGxMTGgoCIAj4'
  + 'KwvzORHxRRXtQgraVBTcSgLCWwvDaRHBdBS8Yvqaa/uTjhamevqCgvp6nQ2FngZ2txd/tAxs'
  + 'qflRwQlZxARMzARE3Q1F4ws79hY+8Ago6voS8voKCwsTPyAID4CUmGcAAAAASUVORK5CYII=';

/** Its size, and the size it is drawn at. Drawn 1:1 on purpose — resizing it
 *  would fold `fitWithin` into the digest, and that is Morph's arithmetic
 *  rather than the browser's. */
export const PROBE_PX = 32;
